/**
 * SessionRoomPage — the B2C-compatible live session room.
 *
 * Route: /session/:sessionId
 *
 * This is the screen every participant sees during a live or open session.
 * It is intentionally separate from every B2B admin surface:
 *   - No feature flag gate (always the current experience)
 *   - No admin tabs, no config panels, no Track nav
 *   - No B2B-only imports or routes
 *
 * The facilitator's Event setup console (/prioritization/sessions/explore)
 * links here via "Go live" or "Open" — this is what everyone in the room sees.
 *
 * Per canon doc 25-deployment-mode-b2b-b2c.md: this route is B2C-safe.
 */

import { useParams, useNavigate, Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { Loader2, ArrowLeft } from 'lucide-react';
import { ParticipantSessionFlow } from '../../features/sessions-participant';
import { useSessionEngineStore } from '../../features/session-engine-v3/store';
import { useSessionStore } from '../../stores/prioritization/sessionStore';
import { eventDesignerHref } from '../../lib/catchballSessionLink';

export default function SessionRoomPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [sessionChecked, setSessionChecked] = useState(false);
  const [sessionExists, setSessionExists] = useState(false);

  // V3 Event context — show the event name and step if this session
  // was launched from the Session Engine setup console.
  const event = useSessionEngineStore((s) => s.event);
  const v3EventId = typeof localStorage !== 'undefined'
    ? localStorage.getItem('v3ActiveEventId')
    : null;
  const isV3Session = !!sessionId && sessionId === event?.id;
  const isV3EventMatch = !!sessionId && sessionId === v3EventId;
  const showEventBanner = isV3Session || isV3EventMatch;

  // Resolve event title from store or localStorage best-effort
  const eventTitle = event?.title ?? null;

  // Verify the session exists before rendering the flow — one-shot, no re-fetch loop
  const fetchSessionById = useSessionStore((s) => s.fetchSessionById);
  const getSession = useSessionStore((s) => s.getSession);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!sessionId || hasFetched.current) return;
    hasFetched.current = true;

    const already = getSession(sessionId);
    if (already) {
      setSessionExists(true);
      setSessionChecked(true);
      return;
    }
    void fetchSessionById(sessionId).then((ok) => {
      setSessionExists(ok);
      setSessionChecked(true);
    });
  }, [sessionId, fetchSessionById, getSession]);

  if (!sessionId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--bg)] text-[var(--text-ter)]">
        <p className="text-sm">No session ID provided.</p>
      </div>
    );
  }

  if (!sessionChecked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--bg)]">
        <Loader2 size={28} className="animate-spin text-[var(--text-dim)]" />
      </div>
    );
  }

  if (!sessionExists) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[var(--bg)] text-[var(--text-ter)]">
        <p className="text-sm">Session brief not found.</p>
        <Link
          to={eventDesignerHref({ resume: sessionId })}
          className="text-xs text-[var(--mustard)] hover:underline"
        >
          Open in Event Designer
        </Link>
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-xs text-[var(--text-dim)] hover:text-[var(--text-sec)]"
        >
          <ArrowLeft size={13} /> Go back
        </button>
      </div>
    );
  }

  // Back path: if launched from the Event console, return there; otherwise go
  // to the participant dashboard.
  const backPath = isV3EventMatch
    ? eventDesignerHref({ resume: sessionId })
    : '/prioritization/sessions';

  return (
    <div className="relative">
      {/* V3 Event context banner — shown when launched from the Event setup console */}
      {showEventBanner && eventTitle && (
        <div className="fixed left-0 right-0 top-0 z-40 flex items-center gap-2 border-b border-[var(--rule)] bg-[var(--bg-raised)]/95 px-4 py-2 text-xs text-[var(--text-sec)] backdrop-blur">
          <button
            onClick={() => navigate(eventDesignerHref({ resume: sessionId }))}
            className="flex items-center gap-1 text-[var(--text-dim)] hover:text-[var(--text-pri)]"
          >
            <ArrowLeft size={12} /> Event setup
          </button>
          <span className="text-[var(--text-dim)]">/</span>
          <span className="font-medium text-[var(--text-pri)]">{eventTitle}</span>
          <span className="ml-auto rounded-full bg-alpine-green-900/50 px-2 py-0.5 text-[10px] font-medium text-alpine-green-400">
            Live
          </span>
        </div>
      )}

      {/* The participant session flow — B2C-safe, no admin chrome */}
      <div className={showEventBanner && eventTitle ? 'pt-9' : ''}>
        <ParticipantSessionFlow
          sessionId={sessionId}
          shell="standalone"
          backPath={backPath}
        />
      </div>
    </div>
  );
}
